"use client";

import { useEffect, useRef, useState } from "react";
import { useSocket } from "@/components/providers/socket-provider";
import { motion, AnimatePresence } from "framer-motion";
import {
    CheckCircle2,
    XCircle,
    Activity,
    Send,
    Clock,
    BarChart3,
    AlertCircle,
    SkipForward
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import dayjs from "dayjs";

interface Stats {
    sent: number;
    failed: number;
    pending: number;
    total: number;
    skipped: number;
    progress: number;
}

interface LogItem {
    message: string;
    time: string;
    type: "success" | "skipped" | "failed";
}

export default function CampaignLive({ data, logs = [] }: { data: any, logs?: LogItem[] }) {
    const { isConnected } = useSocket();
    const isCompleted = data?.status === "end" || data?.progress === 100;

    const [stats, setStats] = useState<Stats>({
        sent: data?.statistics?.sent || 0,
        failed: data?.statistics?.failed || 0,
        pending: data?.statistics?.pending || 0,
        total: data?.statistics?.total || 0,
        skipped: data.statistics.skipped || 0,
        progress: data?.statistics?.progress || 0,
    });

    const logRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (data?.statistics) {
            setStats({
                sent: data.statistics.sent || 0,
                failed: data.statistics.failed || 0,
                pending: data.statistics.pending || 0,
                total: data.statistics.total || 0,
                skipped: data.statistics.skipped || 0,
                progress: data.statistics.progress || 0,
            });
        }
    }, [data.statistics]);

    useEffect(() => {
        if (logRef.current) {
            logRef.current.scrollTo({
                top: 0,
                behavior: "smooth",
            });
        }
    }, [logs]);

    return (
        <div className="space-y-6">
            {/* Connection Status & Badge */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <AnimatePresence mode="wait">
                        {isCompleted ? (
                            <motion.div
                                key="completed-badge"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-2 px-2.5 py-1 bg-emerald-500/10 text-emerald-500 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-emerald-500/20"
                            >
                                <CheckCircle2 className="h-3 w-3" />
                                Completed
                            </motion.div>
                        ) : (
                            <motion.div
                                key="live-badge"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex items-center gap-2 px-2.5 py-1 bg-rose-500/10 text-rose-500 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-rose-500/20"
                            >
                                <span className="flex h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                                Live
                            </motion.div>
                        )}
                    </AnimatePresence>
                    {!isConnected && !isCompleted && (
                        <span className="text-[10px] text-amber-500 font-medium animate-pulse">
                            Reconnecting...
                        </span>
                    )}
                </div>
                <div className="text-[10px] font-medium text-zinc-500 uppercase tracking-tight">
                    {dayjs().format("MMM DD, YYYY · HH:mm:ss")}
                </div>
            </div>

            {/* Premium Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    { label: "Recipients", value: stats.total, icon: Send, color: "from-indigo-500 to-blue-500", shadow: "shadow-indigo-500/5" },
                    { label: "Success", value: stats.sent, icon: CheckCircle2, color: "from-emerald-500 to-teal-500", shadow: "shadow-emerald-500/5" },
                    { label: "Skipped", value: stats.skipped, icon: SkipForward, color: "from-amber-500 to-yellow-500", shadow: "shadow-amber-500/5" },
                    { label: "Failed", value: stats.failed, icon: XCircle, color: "from-rose-500 to-orange-500", shadow: "shadow-rose-500/5" },
                    { label: "Remaining", value: stats.pending, icon: Clock, color: "from-amber-500 to-yellow-500", shadow: "shadow-amber-500/5" },
                ].map((item, i) => (
                    <motion.div
                        key={item.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05, type: "spring", stiffness: 100 }}
                        whileHover={{ y: -2, transition: { duration: 0.2 } }}
                        className={`relative group p-4 rounded-2xl bg-white dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-white/5 ${item.shadow} hover:shadow-lg transition-all duration-300 overflow-hidden`}
                    >
                        <div className={`absolute top-0 right-0 p-3 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity`}>
                            <item.icon className="h-10 w-10" />
                        </div>
                        <div className="relative z-10 space-y-1.5">
                            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg shadow-current/10`}>
                                <item.icon className="h-3.5 w-3.5 text-white" />
                            </div>
                            <div>
                                <p className="text-[9px] text-zinc-500 font-bold uppercase tracking-widest">{item.label}</p>
                                <p className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                                    {item.value.toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Central Progress Section */}
            <motion.div
                initial={{ opacity: 0, scale: 0.99 }}
                animate={{ opacity: 1, scale: 1 }}
                className="relative p-7 rounded-[1.5rem] bg-zinc-950 shadow-2xl overflow-hidden border border-white/5"
            >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(124,58,237,0.1),transparent_70%)]" />
                    <Activity className="absolute bottom-[-20%] right-[-5%] h-56 w-56 text-white/[0.03] rotate-12" />
                </div>

                <div className="relative z-10">
                    <div className="flex justify-between items-center mb-5">
                        <div className="space-y-1">
                            <h3 className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.25em]">
                                {isCompleted ? "Final Status" : "Live Trajectory"}
                            </h3>
                            <div className="flex items-baseline gap-1">
                                <span className="text-xl font-black text-white tracking-tighter">{stats.progress}</span>
                                <span className="text-sm font-bold text-zinc-600">%</span>
                            </div>
                        </div>
                        <div className="p-3 bg-white/5 backdrop-blur-md rounded-xl border border-white/10">
                            <BarChart3 className="h-5 w-5 text-indigo-400" />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <div className="relative h-2.5 bg-zinc-900 rounded-full overflow-hidden border border-white/5">
                            <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${stats.progress}%` }}
                                transition={{ type: "spring", bounce: 0, duration: 1.5 }}
                                className="h-full bg-gradient-to-r from-indigo-600 via-violet-500 to-fuchsia-500 rounded-full relative group"
                            >
                                <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.15),transparent)] bg-[length:30px_100%] animate-[shimmer_2s_infinite]" />
                            </motion.div>
                        </div>
                        <div className="flex justify-between text-[9px] font-bold text-zinc-600 uppercase tracking-widest px-0.5">
                            <span>Initialization</span>
                            <span>{isCompleted ? "Touchdown" : "Orbiting"}</span>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Real-time Activity Feed */}
            <div className="rounded-[1.5rem] border border-zinc-200 dark:border-white/5 bg-white dark:bg-zinc-900/30 backdrop-blur-xl overflow-hidden shadow-xl">
                <div className="px-6 py-4 border-b border-zinc-100 dark:border-white/5 flex items-center justify-between bg-zinc-50/30 dark:bg-transparent">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-indigo-500/10 rounded-md">
                            <Activity className="h-3.5 w-3.5 text-indigo-500" />
                        </div>
                        <h3 className="font-bold text-xs text-zinc-900 dark:text-zinc-100 uppercase tracking-wide">
                            {isCompleted ? "Audit Trail" : "Frequency Stream"}
                        </h3>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <div className="h-1 w-1 rounded-full bg-indigo-500 animate-pulse" />
                        <span className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest">
                            {logs.length} Operations
                        </span>
                    </div>
                </div>

                <div
                    ref={logRef}
                    className="h-[280px] overflow-y-auto p-5 space-y-2.5 scroll-smooth scrollbar-thin scrollbar-thumb-zinc-200 dark:scrollbar-thumb-white/5 hover:scrollbar-thumb-indigo-500/20 transition-colors"
                >
                    <AnimatePresence initial={false}>
                        {logs.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="h-full flex flex-col items-center justify-center text-zinc-400 space-y-3"
                            >
                                <div className="p-3 bg-zinc-100 dark:bg-white/5 rounded-full">
                                    <Clock className="h-7 w-7 opacity-20" />
                                </div>
                                <div className="text-center">
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Synchronizing</p>
                                    <p className="text-[9px] opacity-20 mt-1">Awaiting data packets...</p>
                                </div>
                            </motion.div>
                        ) : (
                            logs.map((log, index) => (
                                <motion.div
                                    key={`${log.time}-${index}`}
                                    initial={{ opacity: 0, x: -5, y: 5, scale: 0.99 }}
                                    animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                                    className={`relative flex items-center gap-3.5 p-3 rounded-xl border transition-all duration-300 group ${log.type === "success"
                                        ? "bg-emerald-500/[0.01] border-emerald-500/5 hover:border-emerald-500/20"
                                        : log.type === "skipped"
                                            ? "bg-amber-500/[0.01] border-amber-500/5 hover:border-amber-500/20"
                                            : "bg-rose-500/[0.01] border-rose-500/5 hover:border-rose-500/20"
                                        }`}
                                >
                                    <div className={`p-1.5 rounded-lg shrink-0 ${log.type === "success"
                                        ? "bg-emerald-500/10 text-emerald-500"
                                        : log.type === "skipped"
                                            ? "bg-amber-500/10 text-amber-500"
                                            : "bg-rose-500/10 text-rose-500"
                                        }`}>
                                        {log.type === "success" ? <CheckCircle2 className="h-3 w-3" /> : log.type === "skipped" ? <SkipForward className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                                    </div>
                                    <div className="flex-1 min-w-0 pr-2">
                                        <p className={`text-[13px] font-semibold truncate tracking-tight transition-colors ${log.type === "success"
                                            ? "text-emerald-950 dark:text-emerald-400 group-hover:text-emerald-500"
                                            : log.type === "skipped"
                                                ? "text-amber-950 dark:text-amber-400 group-hover:text-amber-500"
                                                : "text-rose-950 dark:text-rose-400 group-hover:text-rose-500"
                                            }`}>
                                            {log.message}
                                        </p>
                                        <div className="flex items-center gap-1.5 mt-0.5">
                                            <span className="text-[9px] font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-widest">
                                                {dayjs(log.time).isValid() ? dayjs(log.time).format("YYYY/MM/DD HH:mm:ss") : log.time}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-md border ${log.type === "success" ? "border-emerald-500/20 text-emerald-500" : log.type === "skipped" ? "border-amber-500/20 text-amber-500" : "border-rose-500/20 text-rose-500"
                                            }`}>
                                            OK
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
}